// Analytics tab › Visuals (spec §8): master-detail with the list split into
// "My Visuals" / "Standardised" tabs driven by the object catalog
// (in_navigation ⇒ standardised — approved visuals live under the shared
// "Standardized" navigation folder), USED/UNUSED badges, and the pending
// standardization-request queue (spec §8.1.2 / §12) on the Standardised tab.
// The queue FAILS SOFT: when the backend is unavailable it simply hides.
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Check, Share2, X } from 'lucide-react'
import VisualDesignerPage from '@/pages/designers/VisualDesignerPage'
import VisualPreviewModal from '@/pages/designers/visual/VisualPreviewModal'
import {
  generateVisualObject,
  saveVisualObject,
  deleteVisualObject,
  listVisualSchemas,
  objectCatalog,
  type CatalogObject,
} from '@/api/designers'
import { blankVisualModel } from '@/pages/designers/visual/helpers'
import {
  listStandardizeRequests,
  reviewStandardizeRequest,
  type StandardizeRequest,
  type StandardizeRequestList,
} from '@/api/standardize'
import ObjectMasterDetail, { type MasterItem } from './ObjectMasterDetail'
import PreviewThumbnail, { bustPreview } from './PreviewThumbnail'
import PublishToMenuDialog, { type PublishTarget } from './PublishToMenuDialog'
import { EmptyStateCoach, TypeChip, errMessage, slugify } from './shared'

type VisualsTab = 'mine' | 'standardised'

function PendingQueue({
  project, list, onReviewed,
}: {
  project: string
  list: StandardizeRequestList
  onReviewed: () => void
}) {
  const pending = list.requests.filter((r) => r.status === 'pending')
  const [acting, setActing] = useState<{ id: string; approve: boolean } | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  if (pending.length === 0) return null

  const submit = async (req: StandardizeRequest, approve: boolean) => {
    setBusy(true)
    try {
      await reviewStandardizeRequest(
        project,
        req.id,
        approve,
        {
          review_note: note.trim() || undefined,
          folder_title: approve ? (req.suggested_folder || undefined) : undefined,
        },
      )
      toast.success(approve
        ? 'Approved — visual published to the shared menu'
        : 'Request rejected — requester notified')
      setActing(null)
      setNote('')
      onReviewed()
    } catch (err) {
      toast.error(errMessage(err, 'Could not submit the review'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      style={{
        border: '1px solid var(--color-warning)',
        background: 'var(--color-warning-weak)',
        borderRadius: 12,
        padding: '10px 11px',
        display: 'grid',
        gap: 8,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-warning)' }}>
        Pending requests — end users asking to standardise their visuals
      </div>
      {pending.map((req) => (
        <div
          key={req.id}
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 9,
            padding: '8px 9px',
            display: 'grid',
            gap: 6,
            background: 'var(--color-bg-secondary)',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600 }}>{req.title}</div>
          <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
            {req.requester}
            {req.suggested_folder ? ` · suggested folder: ${req.suggested_folder}` : ''}
          </div>
          {req.note && (
            <div
              style={{
                fontSize: 14, color: 'var(--color-text-secondary)', fontStyle: 'italic',
                overflow: 'hidden', textOverflow: 'ellipsis',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              }}
            >
              “{req.note}”
            </div>
          )}
          {list.can_review && (
            acting?.id === req.id ? (
              <div style={{ display: 'grid', gap: 6 }}>
                <input
                  autoFocus
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={acting.approve
                    ? 'Note to the requester (optional)'
                    : 'Why this was rejected (optional)'}
                  style={{
                    width: '100%', padding: '6px 8px', fontSize: 14,
                    border: '1px solid var(--color-border)', borderRadius: 6,
                    background: 'var(--color-input-bg)', color: 'var(--color-text)',
                  }}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    className={acting.approve ? 'btn btn-primary btn-sm' : 'btn btn-danger btn-sm'}
                    disabled={busy}
                    onClick={() => void submit(req, acting.approve)}
                  >
                    {busy ? 'Sending…' : acting.approve ? 'Confirm approve' : 'Confirm reject'}
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={busy}
                    onClick={() => { setActing(null); setNote('') }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => { setActing({ id: req.id, approve: true }); setNote('') }}
                >
                  <Check size={13} />
                  Approve
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ color: 'var(--color-error)' }}
                  onClick={() => { setActing({ id: req.id, approve: false }); setNote('') }}
                >
                  <X size={13} />
                  Reject
                </button>
              </div>
            )
          )}
        </div>
      ))}
    </div>
  )
}

export default function VisualsSection({
  project, registerLeaveGuard, initialObjName, openNonce, initialTab, onGotoSection, onChanged,
}: {
  project: string
  registerLeaveGuard?: (fn: ((proceed: () => void) => void) | null) => void
  initialObjName?: string
  openNonce?: number
  initialTab?: VisualsTab
  onGotoSection: (section: 'schema') => void
  onChanged?: () => void
}) {
  const [visuals, setVisuals] = useState<CatalogObject[]>([])
  const [schemaCount, setSchemaCount] = useState<number | null>(null)
  const [queue, setQueue] = useState<StandardizeRequestList | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<VisualsTab>(initialTab || 'mine')

  // Standardise flow (spec §12): multi-select on My Visuals + a dialog that
  // publishes the picked visuals to the shared menu via publish-to-navigation.
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set())
  const [dialogTargets, setDialogTargets] = useState<PublishTarget[] | null>(null)
  // "View" from a card → the shared full-screen preview modal (loads the SAVED
  // visual from disk; the same PreviewBody the designer Preview / navigation use).
  const [viewObj, setViewObj] = useState<string | null>(null)

  useEffect(() => {
    if (initialTab) setTab(initialTab)
  }, [initialTab, openNonce])

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [catRes, schemaRes, queueRes] = await Promise.allSettled([
        objectCatalog(project, 'V'),
        listVisualSchemas(project),
        listStandardizeRequests(project), // already fail-soft (null on error)
      ])
      if (catRes.status === 'rejected') throw catRes.reason
      setVisuals(catRes.value.objects || [])
      setSchemaCount(schemaRes.status === 'fulfilled' ? (schemaRes.value.schemas || []).length : null)
      setQueue(queueRes.status === 'fulfilled' ? queueRes.value : null)
    } catch (err) {
      setError(errMessage(err, 'Could not load visuals'))
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void refresh() }, [refresh])

  // Standardised = published to the shared navigation menu (the approval
  // flow files them under the reserved "Standardized" folder).
  const mine = visuals.filter((v) => !v.in_navigation)
  const standardised = visuals.filter((v) => v.in_navigation)
  const shown = tab === 'mine' ? mine : standardised

  // Selection only makes sense on My Visuals (already-standardised ones are
  // on the menu). Drop picks that standardised/vanished under us.
  const eligible = useMemo(() => new Set(mine.map((v) => v.obj_name)), [mine])
  useEffect(() => {
    setPicked((prev) => {
      const next = new Set([...prev].filter((id) => eligible.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [eligible])

  const toTarget = useCallback((objName: string): PublishTarget | null => {
    const v = visuals.find((x) => x.obj_name === objName)
    return v ? { obj_name: v.obj_name, obj_type: 'V', title: v.title || v.obj_name } : null
  }, [visuals])

  const openStandardise = (objNames: string[]) => {
    const targets = objNames
      .map(toTarget)
      .filter((t): t is PublishTarget => t !== null)
    if (targets.length === 0) {
      toast('Select at least one visual first')
      return
    }
    setDialogTargets(targets)
  }

  const items: MasterItem[] = shown.map((v) => ({
    id: v.obj_name,
    name: v.title || v.obj_name,
    subtitle: v.obj_name,
    badge: v.used
      ? { text: 'USED', tone: 'success' as const }
      : { text: 'UNUSED', tone: 'muted' as const },
  }))

  const handleCreate = async (name: string, description: string): Promise<string | null> => {
    const objName = slugify(name)
    try {
      const res = await generateVisualObject({
        project,
        obj_name: objName,
        description: description || name,
      })
      const failed = res.verification?.failures?.length || 0
      if (failed > 0) {
        toast.error(
          `Visual "${name}" drafted but still has ${failed} issue${failed === 1 ? '' : 's'} when run — open it and check the data/criteria`,
          { duration: 6000 },
        )
      } else {
        toast.success(`Visual "${name}" drafted${res.verification ? ' & verified' : ''} — review and save it`)
      }
      setTab('mine')
      await refresh()
      onChanged?.()
      return objName
    } catch (err) {
      toast.error(errMessage(err, 'Could not generate the visual'))
      return null
    }
  }

  // Manual path: persist a blank visual (no AI) and open the designer so the
  // user picks the data source, chart type and columns themselves.
  const handleCreateManual = async (name: string, _description: string): Promise<string | null> => {
    const objName = slugify(name)
    try {
      await saveVisualObject(project, objName, blankVisualModel(objName))
      toast.success(`Visual "${name}" created — pick a data source and chart`)
      setTab('mine')
      await refresh()
      onChanged?.()
      return objName
    } catch (err) {
      toast.error(errMessage(err, 'Could not create the visual'))
      return null
    }
  }

  const tabButton = (key: VisualsTab, label: string, count: number) => (
    <button
      key={key}
      onClick={() => setTab(key)}
      style={{
        background: 'transparent',
        border: 'none',
        borderBottom: tab === key ? '2px solid var(--color-accent)' : '2px solid transparent',
        color: tab === key ? 'var(--color-text)' : 'var(--color-text-secondary)',
        fontWeight: tab === key ? 600 : 500,
        fontSize: 15,
        padding: '10px 2px',
        cursor: 'pointer',
      }}
    >
      {label} ({count})
    </button>
  )

  const pendingCount = queue ? queue.requests.filter((r) => r.status === 'pending').length : 0

  return (
    <>
    <ObjectMasterDetail
      project={project}
      typeCode="V"
      heading="Visuals"
      subCopy="Charts, tables and KPIs. Standardised visuals live on the shared menu for everyone; the rest are yours to shape."
      items={items}
      loading={loading}
      error={error}
      onRetry={() => void refresh()}
      newLabel="New visual"
      newDialogTitle="New visual"
      namePlaceholder="e.g. Monthly sales by region"
      descPlaceholder="e.g. A column chart of net sales by month for the last 12 months, split by region."
      createButtonLabel="Generate with AI"
      onCreate={handleCreate}
      onCreateManual={handleCreateManual}
      manualButtonLabel="Create manually"
      registerLeaveGuard={registerLeaveGuard}
      initialObjName={initialObjName}
      openNonce={openNonce}
      renderPreview={(item) => (
        <PreviewThumbnail project={project} objName={item.id} kind="V" />
      )}
      onEditorClose={(obj) => bustPreview(project, 'V', obj)}
      onView={(obj) => setViewObj(obj)}
      deleteConfig={{
        objType: 'V',
        objLabel: 'visual',
        doDelete: async (id) => { await deleteVisualObject(project, id) },
        onDeleted: () => { void refresh(); onChanged?.() },
      }}
      itemSelection={tab === 'mine' ? {
        selected: picked,
        eligible,
        onToggle: (id) => setPicked((prev) => {
          const next = new Set(prev)
          if (next.has(id)) next.delete(id)
          else next.add(id)
          return next
        }),
      } : undefined}
      headerActions={tab === 'mine' && mine.length > 0 ? (
        <button
          className="btn btn-secondary"
          onClick={() => openStandardise([...picked])}
        >
          <Share2 size={14} />
          Standardise selected{picked.size > 0 ? ` (${picked.size})` : ''}
        </button>
      ) : undefined}
      detailToolbar={(objName) => {
        const v = visuals.find((x) => x.obj_name === objName)
        if (!v || v.in_navigation) return null
        return (
          <button
            className="btn btn-secondary btn-sm"
            title="Publish this visual to the shared menu"
            onClick={() => openStandardise([objName])}
          >
            <Share2 size={13} />
            Standardise…
          </button>
        )
      }}
      listHeader={
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 16, borderBottom: '1px solid var(--color-border)' }}>
            {tabButton('mine', 'My Visuals', mine.length)}
            {tabButton('standardised', 'Standardised', standardised.length)}
            {pendingCount > 0 && tab !== 'standardised' && (
              <span
                style={{
                  alignSelf: 'center', marginLeft: 'auto', fontSize: 13, fontWeight: 600,
                  color: 'var(--color-warning)', whiteSpace: 'nowrap',
                }}
              >
                {pendingCount} pending
              </span>
            )}
          </div>
          {tab === 'standardised' && queue && (
            <PendingQueue
              project={project}
              list={queue}
              onReviewed={() => { void refresh(); onChanged?.() }}
            />
          )}
        </div>
      }
      emptyState={(openNew) =>
        schemaCount === 0 ? (
          <EmptyStateCoach
            icon={<TypeChip code="V" size={26} />}
            title="A visual needs a Data Model first"
            body="Charts draw their columns from a Data Model (schema design) or a Business Area. Create a Data Model first, then come back and build your first chart."
            action={
              <button className="btn btn-primary btn-sm" onClick={() => onGotoSection('schema')}>
                Go to Schema Design
              </button>
            }
          />
        ) : (
          <EmptyStateCoach
            icon={<TypeChip code="V" size={26} />}
            title="No visuals yet"
            body="A visual is one chart, table or KPI. Generate one from a plain-language description — it lands here for you to fine-tune."
            action={
              <button className="btn btn-primary btn-sm" onClick={openNew}>
                + New visual
              </button>
            }
          />
        )
      }
      renderDesigner={(objName, common) => (
        <VisualDesignerPage key={`${project}:${objName}`} {...common} />
      )}
    />
    <PublishToMenuDialog
      open={dialogTargets !== null}
      mode="standardise"
      project={project}
      targets={dialogTargets || []}
      onClose={() => setDialogTargets(null)}
      onPublished={() => {
        setPicked(new Set())
        setTab('standardised')
        void refresh()
        onChanged?.()
      }}
    />
    <VisualPreviewModal
      open={viewObj !== null}
      projectName={project}
      objectName={viewObj || ''}
      onClose={() => setViewObj(null)}
    />
    </>
  )
}
